import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/track";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import PromoBanner from "@/components/PromoBanner";
import {
  ArrowRight,
  Gauge,
  Receipt,
  AlertOctagon,
  CalendarCheck,
  Check,
  Briefcase,
  Users,
  Building2,
  Layers,
  Clock,
} from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent("homepage_viewed");
  }, []);

  const handleLogin = (source = "cta") => {
    trackEvent("cta_clicked", { source });
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePrimaryCTA = (source = "primary") => {
    if (user) navigate("/dashboard");
    else {
      trackEvent("cta_clicked", { source });
      navigate("/signup");
    }
  };

  const handleSamplePreview = () => {
    trackEvent("sample_dashboard_viewed");
    if (user) navigate("/dashboard");
    else navigate("/sample-dashboard");
  };

  return (
    <div className="min-h-screen bg-[#F8F6F1] text-[#1F2326]">
      <PromoBanner />
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#F8F6F1]/85 border-b border-[#E4DFD2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <Logo size={28} showTagline />
          </Link>
          <nav className="flex items-center gap-2">
            <Link
              to="/pricing"
              data-testid="landing-pricing-link"
              onClick={() => trackEvent("cta_clicked", { source: "nav_pricing" })}
              className="px-3 py-2 text-sm text-[#69747C] hover:text-[#1F2326] transition-colors"
            >
              Pricing
            </Link>
            {user ? (
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="landing-dashboard-btn"
                className="bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] px-4 py-2 rounded-lg font-medium text-sm transition-colors inline-flex items-center gap-1.5"
              >
                Open dashboard <ArrowRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <>
                <button
                  onClick={() => { trackEvent("cta_clicked", { source: "nav_login" }); navigate("/login"); }}
                  data-testid="landing-login-btn"
                  className="px-3 py-2 text-sm text-[#1F2326] hover:text-[#B89B5E] font-medium transition-colors"
                >
                  Log in
                </button>
                <button
                  onClick={() => { trackEvent("cta_clicked", { source: "nav_signup" }); navigate("/signup"); }}
                  data-testid="landing-signin-btn"
                  className="bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                >
                  Start free trial
                </button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F0EADA] border border-[#E4DFD2] text-[#7A5E1F] text-xs font-medium mb-6">
              <Clock className="w-3 h-3" /> For consultants, studios, and agencies
            </div>
            <h1 className="font-display text-[44px] sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.04] text-[#1F2326]">
              Know when clients{" "}
              <span className="text-[#B89B5E]">go over hours.</span>
            </h1>
            <p className="mt-6 text-lg text-[#69747C] leading-relaxed max-w-xl">
              Track retainer fees, included hours, logged time, and delivery costs. See which clients are on track, close to the limit, or losing money.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={() => handlePrimaryCTA("hero_primary")}
                data-testid="landing-cta-primary"
                className="bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] px-5 py-3 rounded-lg font-medium text-sm transition-colors inline-flex items-center gap-2 shadow-sm"
              >
                Start free trial <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={handleSamplePreview}
                data-testid="landing-cta-secondary"
                className="bg-white text-[#1F2326] border border-[#E4DFD2] hover:bg-[#FBFAF6] hover:border-[#D6CFB9] px-5 py-3 rounded-lg font-medium text-sm transition-colors"
              >
                View sample dashboard
              </button>
            </div>
            <p className="mt-4 text-xs text-[#69747C]">14-day free trial. No credit card needed.</p>
          </div>

          {/* Mock dashboard preview */}
          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative">
              <div className="absolute -inset-6 -z-10 bg-gradient-to-br from-[#F0EADA] via-transparent to-[#E4DFD2] rounded-3xl blur-2xl opacity-70" />
              <div className="bg-white border border-[#E4DFD2] rounded-2xl p-5 shadow-[0_24px_60px_-20px_rgba(31,35,38,0.18)]">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-[10px] tracking-[0.1em] uppercase text-[#B89B5E] font-semibold">Live preview</div>
                    <div className="font-display text-[#1F2326] text-base font-semibold">This month</div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#EFF3EE] border border-[#C5D5C8] text-[#42624B] text-[11px] font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#6F9775]" /> On Track
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-[#EDE7D6] rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#69747C] font-semibold">Revenue</div>
                    <div className="font-display text-2xl font-semibold text-[#1F2326] mt-1">$12.5k</div>
                  </div>
                  <div className="border border-[#EDE7D6] rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#69747C] font-semibold">Profit</div>
                    <div className="font-display text-2xl font-semibold text-[#42624B] mt-1">$6.4k</div>
                  </div>
                  <div className="border border-[#EDE7D6] rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#69747C] font-semibold">Utilization</div>
                    <div className="font-display text-2xl font-semibold text-[#1F2326] mt-1">84%</div>
                  </div>
                  <div className="border border-[#EDE7D6] rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#69747C] font-semibold">Hours</div>
                    <div className="font-display text-2xl font-semibold text-[#1F2326] mt-1">113h</div>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  {[
                    { n: "Brightline Studio", s: "on_track", v: "60%" },
                    { n: "North Peak Dental", s: "close_to_limit", v: "96%" },
                    { n: "Luma Wellness", s: "losing_money", v: "130%" },
                  ].map((r) => (
                    <div key={r.n} className="flex items-center justify-between border border-[#EDE7D6] rounded-lg px-3 py-2">
                      <div className="text-sm font-medium text-[#1F2326]">{r.n}</div>
                      <div className="flex items-center gap-3">
                        <div className="text-xs text-[#69747C] tabular-nums">{r.v}</div>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                            r.s === "on_track"
                              ? "bg-[#EFF3EE] text-[#42624B] border-[#C5D5C8]"
                              : r.s === "close_to_limit"
                              ? "bg-[#FAF4E8] text-[#7A5E1F] border-[#E6D1A2]"
                              : "bg-[#FAEEED] text-[#7E332D] border-[#E7BFBA]"
                          }`}
                        >
                          <span
                            className={`w-1 h-1 rounded-full ${
                              r.s === "on_track"
                                ? "bg-[#6F9775]"
                                : r.s === "close_to_limit"
                                ? "bg-[#C99A4A]"
                                : "bg-[#C76055]"
                            }`}
                          />
                          {r.s === "on_track" ? "On Track" : r.s === "close_to_limit" ? "Close to Limit" : "Losing Money"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem section */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 border-t border-[#E4DFD2]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.1em] uppercase font-semibold text-[#B89B5E]">The problem</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2 text-[#1F2326]">
              Retainers are easy to over-service.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4 text-[#1F2326] text-base leading-relaxed">
            <p>
              A client pays a fixed monthly fee. The work grows. Extra calls, extra edits, extra tasks, extra quick asks. By the end of the month, the retainer may look fine on paper but cost too much time to deliver.
            </p>
            <p className="text-[#69747C]">
              RetainerTracker shows the numbers clearly, so you can act before your money is gone.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 border-t border-[#E4DFD2]">
        <div className="text-[11px] tracking-[0.1em] uppercase font-semibold text-[#B89B5E]">What it does</div>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2 text-[#1F2326] max-w-2xl">
          One screen, four answers.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          {[
            {
              icon: Gauge,
              title: "Track hours against each retainer.",
              body: "Set the monthly fee, included hours, and logged hours for each client.",
            },
            {
              icon: Receipt,
              title: "See real delivery cost.",
              body: "Add your internal hourly cost and see how much each client costs to service.",
            },
            {
              icon: AlertOctagon,
              title: "Spot risky clients.",
              body: "Each client gets a clear status: On Track, Close to Limit, or Losing Money.",
            },
            {
              icon: CalendarCheck,
              title: "Review every month.",
              body: "Use the dashboard before month-end to decide what needs to change.",
            },
          ].map((f) => (
            <div key={f.title} className="bg-white border border-[#E4DFD2] rounded-xl p-6 hover:border-[#D6CFB9] transition-colors">
              <div className="w-10 h-10 rounded-lg bg-[#1F2326] flex items-center justify-center mb-4">
                <f.icon className="w-4 h-4 text-[#B89B5E]" strokeWidth={2} />
              </div>
              <div className="font-display text-lg font-semibold text-[#1F2326]">{f.title}</div>
              <p className="text-sm text-[#69747C] mt-2 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits / ICP */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 border-t border-[#E4DFD2]">
        <div className="text-[11px] tracking-[0.1em] uppercase font-semibold text-[#B89B5E]">Who it is for</div>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2 text-[#1F2326] max-w-2xl">
          Made for retainer-based service businesses.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {[
            {
              icon: Briefcase,
              tag: "Solo consultants",
              line: "Know if each client is worth the time.",
            },
            {
              icon: Users,
              tag: "Studios",
              line: "See which retained clients are taking more time than planned.",
            },
            {
              icon: Building2,
              tag: "Agencies",
              line: "Find unprofitable retainers before they become normal.",
            },
          ].map((b) => (
            <div key={b.tag} className="bg-[#FBFAF6] border border-[#EDE7D6] rounded-xl p-6">
              <b.icon className="w-5 h-5 text-[#B89B5E]" strokeWidth={2} />
              <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C] mt-3">
                {b.tag}
              </div>
              <div className="font-display text-lg font-semibold text-[#1F2326] mt-1">
                {b.line}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 border-t border-[#E4DFD2]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.1em] uppercase font-semibold text-[#B89B5E]">Scope</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2 text-[#1F2326]">
              Not project management. Not another timer.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-[#1F2326] text-base leading-relaxed">
              RetainerTracker does not replace your project tool, time tracker, or invoice system. It gives you one simple view of retainer profit.
            </p>
            <p className="text-[#69747C] mt-4 text-sm font-medium">Use it to answer:</p>
            <ul className="mt-3 space-y-2.5">
              {[
                "Which clients are over hours?",
                "Which retainers have low margin?",
                "Which clients need a scope or price review?",
                "Which accounts are still worth the work?",
              ].map((q) => (
                <li key={q} className="flex items-start gap-3 text-sm text-[#1F2326]">
                  <Check className="w-4 h-4 text-[#B89B5E] flex-none mt-0.5" strokeWidth={2.5} />
                  {q}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="bg-[#1F2326] text-[#F8F6F1] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-[#B89B5E]/25 blur-3xl" />
          <div className="relative max-w-2xl">
            <Layers className="w-7 h-7 text-[#B89B5E] mb-5" />
            <h3 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              See retainer profit before month-end.
            </h3>
            <p className="text-[#C9C2B0] mt-4 text-base">
              Start the 14-day free trial. Five sample clients are already loaded so you can see the dashboard in action.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button
                onClick={() => handlePrimaryCTA("bottom_cta")}
                data-testid="landing-cta-bottom"
                className="bg-[#B89B5E] text-[#1F2326] hover:bg-[#A78946] px-5 py-3 rounded-lg font-medium text-sm transition-colors inline-flex items-center gap-2"
              >
                {user ? "Open dashboard" : "Start free trial"}
                <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/pricing"
                onClick={() => trackEvent("cta_clicked", { source: "bottom_pricing" })}
                className="bg-white/10 text-white hover:bg-white/15 border border-white/15 px-5 py-3 rounded-lg font-medium text-sm transition-colors"
              >
                See pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SEO / AI-search positioning sections ===== */}
      <section className="py-16 lg:py-20 border-t border-[#E4DFD2]" data-testid="landing-positioning">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <p className="text-base sm:text-lg leading-relaxed text-[#1F2326]">
            Retainer Tracker is a simple client retainer tracking app for consultants, studios, and small agencies.
          </p>
          <p className="text-base sm:text-lg leading-relaxed text-[#1F2326] mt-3">
            It helps you track retainer fees, included hours, logged time, delivery cost, profit, margin, and client status.
          </p>
          <p className="text-base sm:text-lg leading-relaxed text-[#1F2326] mt-3">
            Use it to see which clients are on track, close to the limit, or losing money.
          </p>
        </div>
      </section>

      <section className="py-14 lg:py-16 bg-[#FBFAF6] border-t border-[#E4DFD2]" data-testid="landing-who-its-for">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tighter text-[#1F2326]">
            Built for small service businesses with monthly retainers
          </h2>
          <p className="text-base text-[#1F2326] mt-4">Retainer Tracker is built for:</p>
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-base text-[#1F2326]">
            {[
              "solo consultants",
              "fractional operators",
              "marketing consultants",
              "SEO consultants",
              "paid media consultants",
              "content consultants",
              "design studios",
              "web studios",
              "small agencies",
            ].map((p) => (
              <li key={p} className="flex items-start gap-2.5">
                <Check className="w-4 h-4 mt-1 text-[#B89B5E] flex-none" strokeWidth={2.5} />
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <p className="text-base text-[#1F2326] mt-5">
            It is for teams that want a simple dashboard, not a large agency system.
          </p>
        </div>
      </section>

      <section className="py-14 lg:py-16 border-t border-[#E4DFD2]" data-testid="landing-what-its-not">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tighter text-[#1F2326]">
            Not a CRM. Not project management. Not another timer.
          </h2>
          <p className="text-base text-[#1F2326] mt-4">
            Retainer Tracker does not replace your CRM, project tool, time tracker, or invoice system.
          </p>
          <p className="text-base text-[#1F2326] mt-3">
            It gives you one clear view of retainer hours, cost, and profit.
          </p>
          <p className="text-base text-[#1F2326] mt-5">Use it to answer:</p>
          <ul className="mt-3 space-y-2 text-base text-[#1F2326]">
            {[
              "Which clients are over hours?",
              "Which retainers have low margin?",
              "Which clients cost too much to service?",
              "Which accounts need a scope or price review?",
            ].map((p) => (
              <li key={p} className="flex items-start gap-2.5">
                <Check className="w-4 h-4 mt-1 text-[#B89B5E] flex-none" strokeWidth={2.5} />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
